import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Send, RefreshCw, MessageSquare, Smartphone } from "lucide-react";
import { toast } from "sonner";

const EVENT_LABELS = {
  "lead.created": "Lead received",
  "application.started": "Application started (OTP verified)",
  "payment.approved": "Payment approved",
  "document.discrepancy": "Document rejected (self-service re-upload link)",
  "document.approved": "Document approved",
  "offer.issued": "Offer letter issued",
};
const CHANNEL_CHOICES = [
  { v: "whatsapp", label: "WhatsApp only" },
  { v: "sms", label: "SMS only" },
  { v: "both", label: "WhatsApp + SMS" },
];
const STATUS_PILL = {
  delivered: "bg-emerald-100 text-emerald-800",
  queued: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
};
const CHAN_BADGE = { whatsapp: "bg-green-100 text-green-800", sms: "bg-blue-100 text-blue-800" };

export default function NotificationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [tmpl, setTmpl] = useState({});
  const [logs, setLogs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [test, setTest] = useState({ event_type: "payment.approved", phone: "", channel: "" });
  const [testOut, setTestOut] = useState(null);
  const [savingTmpl, setSavingTmpl] = useState(false);
  const [sending, setSending] = useState(false);

  const loadCfg = useCallback(() => {
    api.get("/notifications/config").then((r) => { setCfg(r.data); setTmpl(r.data.templates); }).catch(() => {});
  }, []);
  const loadLogs = useCallback(() => {
    api.get("/notifications/logs", { params: { limit: 50 } }).then((r) => setLogs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadCfg(); loadLogs(); }, [loadCfg, loadLogs]);

  const patch = async (body) => {
    try {
      const { data } = await api.put("/notifications/config", body);
      setCfg((c) => ({ ...data, provider_status: c?.provider_status, placeholders: c?.placeholders, event_types: c?.event_types }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadCfg(); }
  };

  const saveTemplates = async () => {
    setSavingTmpl(true);
    try {
      const { data } = await api.put("/notifications/config", { templates: tmpl });
      setCfg((c) => ({ ...data, provider_status: c?.provider_status, placeholders: c?.placeholders }));
      setTmpl(data.templates);
      toast.success("Templates saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTmpl(false); }
  };

  const sendTest = async () => {
    setSending(true); setTestOut(null);
    try {
      const { data } = await api.post("/notifications/test", {
        event_type: test.event_type, phone: test.phone,
        channel: test.channel === "__cfg__" ? "" : test.channel,
      });
      setTestOut(data);
      toast.success(`Dispatched ${data.dispatched} message(s)`);
      loadLogs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const events = useMemo(() => cfg ? Object.keys(EVENT_LABELS) : [], [cfg]);

  if (!cfg) return <div className="py-16 flex justify-center"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;

  const provHint = (ch, name) => cfg.provider_status?.[ch]?.[name] === false && name !== "mock"
    ? <span className="text-xs text-amber-600 ml-2">not configured — sends via mock</span> : null;

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      {/* master + providers */}
      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-800">Outbound notifications</div>
            <div className="text-sm text-slate-500">Fire WhatsApp / SMS on lead and payment lifecycle events.</div>
          </div>
          <Switch data-testid="notif-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => patch({ enabled: v })} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <div>
            <Label className="text-xs flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> WhatsApp provider</Label>
            <div className="flex items-center mt-1">
              <Select value={cfg.providers.whatsapp} onValueChange={(v) => patch({ providers: { whatsapp: v } })}>
                <SelectTrigger data-testid="notif-wa-provider" className="w-[190px] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(cfg.provider_status?.whatsapp || { mock: true }).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              {provHint("whatsapp", cfg.providers.whatsapp)}
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> SMS provider</Label>
            <div className="flex items-center mt-1">
              <Select value={cfg.providers.sms} onValueChange={(v) => patch({ providers: { sms: v } })}>
                <SelectTrigger data-testid="notif-sms-provider" className="w-[190px] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(cfg.provider_status?.sms || { mock: true }).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              {provHint("sms", cfg.providers.sms)}
            </div>
          </div>
        </div>
      </Card>

      {/* per-event triggers */}
      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200"><h3 className="font-semibold text-slate-800">Event triggers</h3></div>
        <div className="divide-y divide-slate-100">
          {events.map((e) => (
            <div key={e} className="p-4 flex flex-wrap items-center gap-4" data-testid={`notif-event-${e}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium text-slate-800">{EVENT_LABELS[e]}</div>
                <div className="text-xs text-slate-400 font-mono">{e}</div>
              </div>
              <Select value={cfg.events[e].channel} onValueChange={(v) => patch({ events: { [e]: { channel: v } } })}>
                <SelectTrigger data-testid={`notif-channel-${e}`} className="w-[180px] h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNEL_CHOICES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Switch data-testid={`notif-toggle-${e}`} checked={!!cfg.events[e].enabled}
                onCheckedChange={(v) => patch({ events: { [e]: { enabled: v } } })} />
            </div>
          ))}
        </div>
      </Card>

      {/* templates */}
      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Message templates</h3>
          <Button size="sm" data-testid="notif-save-templates" onClick={saveTemplates} disabled={savingTmpl} className="bg-brand">
            {savingTmpl ? "Saving…" : "Save templates"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(cfg.placeholders || []).map((p) => (
            <code key={p} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{`{{${p}}}`}</code>
          ))}
        </div>
        {events.map((e) => (
          <div key={e} className="border border-slate-200 rounded-lg p-3">
            <div className="text-sm font-medium text-slate-700 mb-2">{EVENT_LABELS[e]}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {["whatsapp", "sms"].map((ch) => (
                <div key={ch}>
                  <Label className="text-xs capitalize">{ch}</Label>
                  <Textarea rows={3} data-testid={`notif-tmpl-${e}-${ch}`} className="mt-1 text-xs font-mono"
                    value={tmpl?.[e]?.[ch] ?? ""}
                    onChange={(ev) => setTmpl((t) => ({ ...t, [e]: { ...(t[e] || {}), [ch]: ev.target.value } }))} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card>

      {/* test dispatcher */}
      <Card className="p-5 border-slate-200" data-testid="notif-test-panel">
        <h3 className="font-semibold text-slate-800 mb-3">Test dispatcher</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Event</Label>
            <Select value={test.event_type} onValueChange={(v) => setTest((t) => ({ ...t, event_type: v }))}>
              <SelectTrigger data-testid="notif-test-event" className="w-[190px] h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(EVENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Channel</Label>
            <Select value={test.channel || "__cfg__"} onValueChange={(v) => setTest((t) => ({ ...t, channel: v }))}>
              <SelectTrigger data-testid="notif-test-channel" className="w-[160px] h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__cfg__">As configured</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input data-testid="notif-test-phone" value={test.phone} inputMode="numeric" placeholder="10-digit"
              onChange={(e) => setTest((t) => ({ ...t, phone: e.target.value }))} className="w-40 h-9 mt-1" />
          </div>
          <Button data-testid="notif-test-send" onClick={sendTest} disabled={sending || test.phone.replace(/\D/g, "").length < 10}
            className="bg-brand">
            <Send className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : "Send test"}
          </Button>
        </div>
        {testOut && (
          <div className="mt-4 space-y-2" data-testid="notif-test-result">
            {testOut.logs.map((l) => (
              <div key={l.id} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={CHAN_BADGE[l.channel]}>{l.channel}</Badge>
                  <Badge className={STATUS_PILL[l.status]}>{l.status}</Badge>
                  <span className="text-xs text-slate-400">via {l.provider}</span>
                </div>
                <div className="text-slate-700">{l.rendered_body}</div>
                <pre className="mt-2 text-[11px] bg-white border border-slate-200 rounded p-2 overflow-x-auto">{JSON.stringify(l.provider_response, null, 2)}</pre>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* log table */}
      <Card className="border-slate-200" data-testid="notif-log-table">
        <div className="p-4 flex items-center justify-between border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Dispatch log</h3>
          <Button size="sm" variant="outline" data-testid="notif-log-refresh" onClick={loadLogs}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Time", "Recipient", "Channel", "Event", "Provider", "Status"].map((h) => (
                  <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No notifications dispatched yet.</TableCell></TableRow>
              )}
              {logs.map((l, i) => (
                <TableRow key={l.id} data-testid={`notif-log-row-${i}`} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setDetail(l)}>
                  <TableCell className="text-xs font-mono text-slate-500">{(l.created_at || "").replace("T", " ").slice(0, 19)}</TableCell>
                  <TableCell className="font-mono text-xs">{l.recipient_phone}</TableCell>
                  <TableCell><Badge className={CHAN_BADGE[l.channel]}>{l.channel}</Badge></TableCell>
                  <TableCell className="text-xs">{l.event_type}</TableCell>
                  <TableCell className="text-xs text-slate-500">{l.provider}</TableCell>
                  <TableCell><Badge className={STATUS_PILL[l.status] || "bg-slate-100 text-slate-600"}>{l.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="notif-log-detail">
          <DialogHeader><DialogTitle>{detail?.event_type} · {detail?.channel}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge className={CHAN_BADGE[detail.channel]}>{detail.channel}</Badge>
                <Badge className={STATUS_PILL[detail.status]}>{detail.status}</Badge>
                <Badge variant="secondary">{detail.provider}</Badge>
                <span className="text-xs text-slate-400 font-mono">{detail.recipient_phone}</span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Rendered body</div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap">{detail.rendered_body}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Provider response</div>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] overflow-x-auto">{JSON.stringify(detail.provider_response, null, 2)}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
